// src/components/CurrencyForm.js

import { useState, useEffect } from 'react';
import toast from "react-hot-toast";
import { getApiErrorLines, translateApiMessage } from "../utils/formErrors";
import axiosInstance from '../utils/axiosInstance';
import '../style/CustomsDeclarationList.css';

const CurrencyForm = () => {
  // Form input states
  const [ssdsshGUID, setSsdsshGUID] = useState('');
  const [urlVCodeInt, setUrlVCodeInt] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [startIndex, setStartIndex] = useState(0);

  // Data and UI states
  const [allocations, setAllocations] = useState([]);
  const [selectedAllocations, setSelectedAllocations] = useState([]);
  const [areAllSelected, setAreAllSelected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveProgress, setSaveProgress] = useState({ current: 0, total: 0 });
  const [saveMessage, setSaveMessage] = useState(null);
  const [saveError, setSaveError] = useState(null);

  // Debugging effect to log state changes
  useEffect(() => {
    console.log('Current Allocations:', allocations);
    console.log('Selected Allocations:', selectedAllocations);
    console.log('Error:', error);
  }, [allocations, selectedAllocations, error]);

  // Fetch currency allocations
  const fetchData = async (e) => {
    e.preventDefault();

    if (!ssdsshGUID || !urlVCodeInt || !pageSize) {
      setError('لطفاً همه فیلدها را پر کنید.');
      return;
    }

    setLoading(true);
    setError(null);
    setAllocations([]);
    setSelectedAllocations([]);
    setAreAllSelected(false);

    const payload = {
      ssdsshGUID: ssdsshGUID.trim(),
      urlVCodeInt: parseInt(urlVCodeInt, 10),
      pPageSize: parseInt(pageSize, 10),
      pStartIndex: parseInt(startIndex, 10),
    };

    console.log('Fetch Payload:', payload);

    try {
      const response = await axiosInstance.post(
        '/GetCurrencyAllocationRequests/',
        payload
      );

      console.log('API Response:', response.data);

      // Check for currency_allocations array
      const fetchedAllocations = Array.isArray(response.data.currency_allocations)
        ? response.data.currency_allocations.map((item) => ({
            row_number: item.row_number,
            carVCodeLng: item.carVCodeLng,
            order_no: item.order_no,
            status: item.status,
            active_status: item.active_status,
            amount: item.amount,
            currency: item.currency,
            create_date_shamsi: item.create_date_shamsi,
            issue_date_shamsi: item.issue_date_shamsi,
            approved_date_shamsi: item.approved_date_shamsi,
            source: item.source,
            request_type: item.request_type,
            branch_code: item.branch_code,
            branch_name: item.branch_name,
          }))
        : [];

      if (fetchedAllocations.length > 0) {
        setAllocations(fetchedAllocations);
        console.log('Mapped Allocations:', fetchedAllocations);
      } else {
        setError('هیچ داده‌ای در پاسخ API یافت نشد.');
        console.log('No allocations found in response.');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.ErrorDesc
        ? translateApiMessage(error.response.data.ErrorDesc)
        : getApiErrorLines(error, {}, 'دریافت تخصیص‌های ارز ناموفق بود.').join(' ');
      setError(errorMsg);
      toast.error(errorMsg);
      console.error('Fetch Error:', errorMsg, error);

      // Fallback to mock data for testing
      const mockAllocations = [
        {
          row_number: 1,
          carVCodeLng: 12086785,
          order_no: '67141842',
          status: 'تخصیص یافته',
          active_status: 'اتمام',
          amount: 9869.54,
          currency: 'دلار آمریکا',
          create_date_shamsi: '1404/06/18',
          issue_date_shamsi: '1404/06/18',
          approved_date_shamsi: '1404/06/24',
          source: 'صادرات خود',
          request_type: 'آزاد',
          branch_code: '2100296',
          branch_name: 'دکتر شریعتی',
        },
        {
          row_number: 2,
          carVCodeLng: 12086739,
          order_no: '72093994',
          status: 'تخصیص یافته',
          active_status: 'اتمام',
          amount: 112794.71,
          currency: 'دلار آمریکا',
          create_date_shamsi: '1404/06/18',
          issue_date_shamsi: '1404/06/18',
          approved_date_shamsi: '1404/06/24',
          source: 'مرکز مبادله ارز و طلا (گروه اول)',
          request_type: 'توافقی',
          branch_code: '2100296',
          branch_name: 'دکتر شریعتی',
        },
      ];
      setAllocations(mockAllocations);
      console.log('Using Mock Data:', mockAllocations);
    } finally {
      setLoading(false);
    }
  };

  // Handle individual allocation selection
  const handleSelectAllocation = (event, allocation) => {
    const { checked } = event.target;
    let updatedSelections = [];

    if (checked) {
      updatedSelections = [...selectedAllocations, allocation];
    } else {
      updatedSelections = selectedAllocations.filter(
        (item) => item.carVCodeLng !== allocation.carVCodeLng
      );
    }

    setSelectedAllocations(updatedSelections);
    setAreAllSelected(updatedSelections.length === allocations.length);
  };

  // Handle "Select All"
  const handleSelectAll = (event) => {
    const { checked } = event.target;
    setAreAllSelected(checked);
    setSelectedAllocations(checked ? [...allocations] : []);
  };

  // Clear parameters
  const handleClearParams = () => {
    if (window.confirm('آیا مطمئن هستید که می‌خواهید پارامترها را پاک کنید؟')) {
      setSsdsshGUID('');
      setUrlVCodeInt('');
      setPageSize(10);
      setStartIndex(0);
      setAllocations([]);
      setSelectedAllocations([]);
      setAreAllSelected(false);
      setError(null);
      setSaveMessage(null);
      setSaveError(null);
      setSaveProgress({ current: 0, total: 0 });
      toast.success('پارامترها پاک شدند.');
    }
  };

  // Save selected allocations
  const handleSaveSelected = async () => {
    if (selectedAllocations.length === 0) {
      setSaveError('لطفاً حداقل یک تخصیص ارز را انتخاب کنید.');
      return;
    }

    setSaveProgress({ current: 0, total: selectedAllocations.length });
    setSaveMessage(null);
    setSaveError(null);
    setLoading(true);

    const failedAllocations = [];
    const successMessages = [];

    for (const [index, allocation] of selectedAllocations.entries()) {
      try {
        const payload = {
          order_no: allocation.order_no,
          carVCode: `VC-1404-${allocation.carVCodeLng}`,
          carAmountMny: allocation.amount.toString(),
          curNameStr: allocation.currency,
          carCreateDateShamsi: allocation.create_date_shamsi,
          carIssueDateShamsi: allocation.issue_date_shamsi,
          carApprovedDateShamsi: allocation.approved_date_shamsi,
          ccsNameStr: allocation.source,
          ccrNameStr: 'بانک ملی',
          crtNameStr: allocation.status,
          branch_code: allocation.branch_code,
          branch_name: allocation.branch_name,
        };

        console.log('Saving allocation payload:', payload);

        const response = await axiosInstance.post(
          '/currency-allocations/upsert/',
          payload
        );

        successMessages.push({
          carVCodeLng: allocation.carVCodeLng,
          message: response.data?.message || 'تخصیص ارز با موفقیت ذخیره شد',
        });
      } catch (error) {
        const reason = error.response?.data?.ErrorDesc
          ? translateApiMessage(error.response.data.ErrorDesc)
          : getApiErrorLines(error, {}, 'خطای ناشناخته').join(' ');

        failedAllocations.push({
          carVCodeLng: allocation.carVCodeLng,
          reason,
        });
        console.error('Error saving allocation:', error);
      }

      setSaveProgress({ current: index + 1, total: selectedAllocations.length });
    }

    setLoading(false);

    if (failedAllocations.length > 0) {
      setSaveError({
        failedAllocations,
        successMessages,
      });
      setSaveMessage(
        `${successMessages.length}/${selectedAllocations.length} تخصیص ارز ثبت شده. ${failedAllocations.length} ثبت نشده.`
      );
    } else {
      setSaveMessage('تمام تخصیص‌های ارز با موفقیت ذخیره شدند.');
      setSaveError(null);
    }
  };

  return (
    <div className="customs-declaration-container mt-24">
      <h2 className="title">دریافت لیست تخصیص ارزها از سامانه</h2>

      {/* Form for inputs */}
      <form className="declaration-form" onSubmit={fetchData}>
        <div className="form-group">
          <label htmlFor="ssdsshGUID">ssdsshGUID:</label>
          <input
            type="text"
            id="ssdsshGUID"
            value={ssdsshGUID}
            onChange={(e) => setSsdsshGUID(e.target.value)}
            placeholder="مثال: 603FF82E-5EAF-4DFA-BBC1-FA0030D686DB"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="urlVCodeInt">urlVCodeInt:</label>
          <input
            type="number"
            id="urlVCodeInt"
            value={urlVCodeInt}
            onChange={(e) => setUrlVCodeInt(e.target.value)}
            placeholder="مثال: 124013"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="pageSize">سایز صفحه:</label>
          <input
            type="number"
            id="pageSize"
            value={pageSize}
            onChange={(e) => setPageSize(e.target.value)}
            placeholder="تعداد موارد هر صفحه (مثال: 10)"
            required
            min="1"
            max="2000"
          />
        </div>
        <div className="form-group">
          <label htmlFor="startIndex">نقطه شروع:</label>
          <input
            type="number"
            id="startIndex"
            value={startIndex}
            onChange={(e) => setStartIndex(e.target.value)}
            placeholder="نقطه شروع (مثال: 0)"
            required
            min="0"
            max="10000"
          />
        </div>
        <div className="form-actions">
          <button type="submit" className="submit-button" disabled={loading}>
            دریافت اطلاعات
          </button>
          <button
            type="button"
            className="clear-button"
            onClick={handleClearParams}
            disabled={loading}
          >
            پاک کردن پارامترها
          </button>
        </div>
      </form>

      {/* Status & Messages */}
      {loading && <p className="loading">در حال بارگذاری...</p>}
      {error && <p className="error">{typeof error === 'string' ? error : JSON.stringify(error)}</p>}
      {ssdsshGUID && urlVCodeInt && (
        <div className="current-params">
          <h3>پارامترهای فعلی:</h3>
          <p>
            <strong>SSDSSH GUID:</strong> {ssdsshGUID}
          </p>
          <p>
            <strong>URL VCode Int:</strong> {urlVCodeInt}
          </p>
        </div>
      )}

      {/* Save Selected Button */}
      <button
        className="save-selected-button"
        onClick={handleSaveSelected}
        disabled={selectedAllocations.length === 0 || loading}
      >
        {loading && saveProgress.total > 0
          ? `در حال ذخیره (${saveProgress.current}/${saveProgress.total})`
          : 'ذخیره تخصیص‌های ارز انتخاب‌شده'}
      </button>

      {/* Progress Bar */}
      {saveProgress.total > 0 && (
        <div className="progress-container">
          <progress value={saveProgress.current} max={saveProgress.total} />
          <p>{`در حال ذخیره ${saveProgress.current}/${saveProgress.total}`}</p>
        </div>
      )}

      {/* Save Messages */}
      {saveMessage && <p className="success">{saveMessage}</p>}
      {saveError && (
        <p className="error">
          {typeof saveError === 'string' ? saveError : JSON.stringify(saveError)}
        </p>
      )}

      {/* Allocations Table */}
      {!loading && !error && allocations.length > 0 && (
        <div className="customdec-list-container">
          <table className="customs-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={areAllSelected}
                  />
                </th>
                <th>ردیف</th>
                <th>کد تخصیص</th>
                <th>شماره سفارش</th>
                <th>وضعیت</th>
                <th>وضعیت فعال</th>
                <th>مقدار</th>
                <th>ارز</th>
                <th>تاریخ ثبت (شمسی)</th>
                <th>تاریخ صدور (شمسی)</th>
                <th>تاریخ تأیید (شمسی)</th>
                <th>منبع</th>
                <th>نوع درخواست</th>
                <th>کد شعبه</th>
                <th>نام شعبه</th>
              </tr>
            </thead>
            <tbody>
              {allocations.map((item, index) => (
                <tr key={item.carVCodeLng || index}>
                  <td>
                    <input
                      type="checkbox"
                      onChange={(e) => handleSelectAllocation(e, item)}
                      checked={selectedAllocations.some(
                        (alloc) => alloc.carVCodeLng === item.carVCodeLng
                      )}
                    />
                  </td>
                  <td>{item.row_number || index + 1}</td>
                  <td>{item.carVCodeLng || 'نامشخص'}</td>
                  <td>{item.order_no || 'نامشخص'}</td>
                  <td>{item.status || 'نامشخص'}</td>
                  <td>{item.active_status || 'نامشخص'}</td>
                  <td>{item.amount || 'نامشخص'}</td>
                  <td>{item.currency || 'نامشخص'}</td>
                  <td>{item.create_date_shamsi || 'نامشخص'}</td>
                  <td>{item.issue_date_shamsi || 'نامشخص'}</td>
                  <td>{item.approved_date_shamsi || 'نامشخص'}</td>
                  <td>{item.source || 'نامشخص'}</td>
                  <td>{item.request_type || 'نامشخص'}</td>
                  <td>{item.branch_code || 'نامشخص'}</td>
                  <td>{item.branch_name || 'نامشخص'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* No Allocations Message */}
      {!loading && !error && allocations.length === 0 && (
        <p className="no-data">هیچ تخصیص ارزی یافت نشد.</p>
      )}
    </div>
  );
};

export default CurrencyForm;